import {skipToken} from '@reduxjs/toolkit/query';
import {
  Card,
  DataStatus,
  Dialog,
  Flag,
  IconButton,
  Table,
  TableRow,
  showNotification,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {memo, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useListTrimFeatureAssignmentsQuery, type FeatureResponseBody} from '@dms/api/new-vehicle';

import type {Nullish} from 'shared/web';

import {PendingChangesBar} from '../../components/PendingChangesBar/PendingChangesBar';
import {PendingChangesBlocker} from '../../components/PendingChangesBlocker/PendingChangesBlocker';
import {AssignmentDialog, type AssignmentValues} from './AssignmentDialog';
import {Cell, column, inclusionFlag, t} from './catalogueUi';
import {PriceWithGross} from './PriceWithGross';
import {updateTrimAssignmentSet} from './updateTrimAssignmentSet';
import {
  useEquipmentMatrixDrafts,
  type CellValue,
  type MatrixTrim,
} from './useEquipmentMatrixDrafts';

type Variant = {id: string; orderCode: string; trimId: string};
type MatrixProps = {
  modelYearId: string;
  trims: MatrixTrim[];
  features: FeatureResponseBody[];
  variants: Variant[];
  currency: string;
  vatRate: string | Nullish;
  canWrite: boolean;
  canViewCost: boolean;
};
type EditedCell = {trim: MatrixTrim; feature: FeatureResponseBody};

const NEXT_VALUE: Record<CellValue, CellValue> = {
  NOT_OFFERED: 'STANDARD',
  STANDARD: 'OPTIONAL',
  OPTIONAL: 'NOT_OFFERED',
};
const valueFlag = (value: CellValue) => inclusionFlag(value === 'NOT_OFFERED' ? null : value);
// Stable identity: the blocker re-registers its router predicate when this changes.
const shouldBlockNavigation = () => true;

const cellName = (feature: string, trim: string, value: CellValue, isUnsaved: boolean) => {
  const options = {feature, trim, inclusion: valueFlag(value).label};
  return isUnsaved
    ? t(
        'entity.newVehicle.catalogue.inclusionCellNameUnsaved',
        '{feature} on {trim}: {inclusion}, unsaved',
        options
      )
    : t(
        'entity.newVehicle.catalogue.inclusionCellName',
        '{feature} on {trim}: {inclusion}',
        options
      );
};

// Platform foundation has no visually hidden element for a live region.
const StatusRegion = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

// A native button: the shared `Clickable` has no disabled a11y contract, and the cell needs a
// focusable, labelled control that Enter and Space activate.
const InclusionButton = styled.button<{$isUnsaved: boolean}>`
  position: relative;
  display: inline-flex;
  padding: 0;
  border: 0;
  border-radius: ${({theme}) => theme.radii.small};
  background: none;
  cursor: pointer;
  user-select: none;
  transition-property: scale, opacity;
  transition-duration: 150ms;
  transition-timing-function: ease-out;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:active:not(:disabled) {
    scale: 0.96;
  }

  &:focus-visible {
    outline: var(--outline-default) ${({theme}) => theme.colors.general.accent};
    outline-offset: var(--outline-offset);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  /* An unsaved value carries a dot in the pending bar's warning colour. */
  &::after {
    content: '';
    display: ${({$isUnsaved}) => ($isUnsaved ? 'block' : 'none')};
    position: absolute;
    top: -${({theme}) => theme.getSize(1)};
    right: -${({theme}) => theme.getSize(1)};
    width: ${({theme}) => theme.getSize(2)};
    height: ${({theme}) => theme.getSize(2)};
    border-radius: ${({theme}) => theme.radii.circular};
    background-color: ${({theme}) => theme.colors.severity.warning};
  }
`;

/**
 * One equipment × trim cell. A click cycles a draft value (Not offered, Standard, Optional); drafts
 * are saved for the whole matrix at once. Cells of the same trim share one cached assignment query,
 * and the API only replaces a trim's set as a whole, so every save PUTs that trim's complete list.
 */
const InclusionCell = memo(function InclusionCell(
  props: Pick<MatrixProps, 'currency' | 'vatRate' | 'canWrite'> & {
    trim: MatrixTrim;
    feature: FeatureResponseBody;
    draft: CellValue | Nullish;
    isSaving: boolean;
    onDraft: (
      trim: MatrixTrim,
      feature: FeatureResponseBody,
      value: CellValue,
      serverValue: CellValue
    ) => void;
    onEditDetails: (cell: EditedCell) => void;
  }
) {
  const query = useListTrimFeatureAssignmentsQuery({trimId: props.trim.id});
  const assignments = query.currentData;
  const assignment = assignments?.find(({featureId}) => featureId === props.feature.id);
  const serverValue: CellValue = assignment?.inclusion ?? 'NOT_OFFERED';
  const value = props.draft ?? serverValue;
  const isUnsaved = isNotNil(props.draft);
  const isConditional = Boolean(
    assignment &&
      (assignment.appliesIf.anyOfVariantIds.length || assignment.appliesIf.allOfFeatureIds.length)
  );

  const cycle = () => props.onDraft(props.trim, props.feature, NEXT_VALUE[value], serverValue);

  return (
    <Cell>
      <VStack spacing={1} align="flex-start">
        {props.canWrite ? (
          <HStack spacing={1} align="center">
            <InclusionButton
              type="button"
              data-testid={`inclusion-${props.trim.id}-${props.feature.id}`}
              aria-label={cellName(props.feature.name, props.trim.name, value, isUnsaved)}
              aria-busy={query.isLoading}
              disabled={isNil(assignments)}
              $isUnsaved={isUnsaved}
              onClick={cycle}
            >
              <Flag {...valueFlag(value)} />
            </InclusionButton>
            <Show when={isNotNil(assignment)}>
              <IconButton
                icon="editor/mode"
                size="small"
                aria-label={t(
                  'entity.newVehicle.catalogue.priceAndConditionsOnTrim',
                  'Price and conditions of {feature} on {trim}',
                  {feature: props.feature.name, trim: props.trim.name}
                )}
                aria-haspopup="dialog"
                isDisabled={isUnsaved || props.isSaving}
                onClick={() => props.onEditDetails({trim: props.trim, feature: props.feature})}
              />
            </Show>
          </HStack>
        ) : (
          <Text size="small" color={assignment ? undefined : 'tertiary'}>
            {valueFlag(serverValue).label}
          </Text>
        )}
        <Show when={Boolean(assignment?.priceDeltaNet)}>
          <PriceWithGross
            amount={assignment?.priceDeltaNet ?? '0.00'}
            currency={props.currency}
            vatRate={props.vatRate}
            size="xSmall"
            align="left"
          />
        </Show>
        <Show when={isConditional}>
          <Text size="xSmall" color="secondary">
            {t('entity.newVehicle.catalogue.conditional', 'Conditional')}
          </Text>
        </Show>
      </VStack>
    </Cell>
  );
});

/** Every equipment item against every trim, so what each trim includes is visible at once. */
export function TrimEquipmentMatrix(props: MatrixProps) {
  const matrix = useEquipmentMatrixDrafts({trims: props.trims, features: props.features});
  // One details dialog for the whole matrix: a cell gets no arrays, so memo holds when the page
  // re-renders on every refetch a save causes.
  const [edited, setEdited] = useState<EditedCell | null>(null);
  const featureIds = props.features.map(({id}) => id);
  const editedAssignment = useListTrimFeatureAssignmentsQuery(
    edited ? {trimId: edited.trim.id} : skipToken
  ).currentData?.find(({featureId}) => featureId === edited?.feature.id);
  // The dialog closes when its trim, equipment item or assignment leaves the matrix meanwhile.
  const openDialog =
    edited &&
    editedAssignment &&
    props.trims.some(({id}) => id === edited.trim.id) &&
    featureIds.includes(edited.feature.id)
      ? {edited, assignment: editedAssignment}
      : null;
  // Forget a closed dialog's cell, so it cannot reopen by itself when that cell comes back.
  if (edited && !openDialog) {
    setEdited(null);
  }

  const saveDetails = (cell: EditedCell, values: AssignmentValues) =>
    matrix
      .replaceAssignments(cell.trim.id, (freshAssignments) =>
        updateTrimAssignmentSet(freshAssignments, cell.feature.id, values, props.canViewCost)
      )
      .then(() => {
        showNotification.success(
          t('entity.newVehicle.catalogue.assignmentsSaved', 'Trim assignments saved')
        );
      });

  return (
    <Card
      title={t('entity.newVehicle.catalogue.equipmentByTrim', 'Equipment by trim')}
      subtitle={t(
        'entity.newVehicle.catalogue.equipmentByTrimHint',
        'Standard is included in the trim price, Optional is sold at the equipment price. Click a cell to change it.'
      )}
    >
      <DataStatus
        isEmpty={!props.trims.length || !props.features.length}
        emptyMessage={t(
          'entity.newVehicle.catalogue.equipmentByTrimEmpty',
          'Add trim levels and equipment first'
        )}
      >
        <Table
          columns={[
            column(t('entity.newVehicle.catalogue.equipment', 'Equipment'), {width: '24%'}),
            ...props.trims.map(({name}) => column(name)),
          ]}
        >
          {props.features.map((feature) => (
            <TableRow key={feature.id}>
              <Cell>
                <VStack spacing={0}>
                  <Text size="small" alternative>
                    {feature.name}
                  </Text>
                  <Show when={!feature.standaloneOrderable}>
                    <Text size="xSmall" color="secondary">
                      {t('entity.newVehicle.rules.packageOnly', 'Package-only')}
                    </Text>
                  </Show>
                </VStack>
              </Cell>
              {props.trims.map((trim) => (
                <InclusionCell
                  key={trim.id}
                  trim={trim}
                  feature={feature}
                  currency={props.currency}
                  vatRate={props.vatRate}
                  canWrite={props.canWrite}
                  isSaving={matrix.isSaving}
                  draft={matrix.drafts[trim.id]?.[feature.id]}
                  onDraft={matrix.setDraft}
                  onEditDetails={setEdited}
                />
              ))}
            </TableRow>
          ))}
        </Table>
      </DataStatus>
      <Dialog
        isOpen={isNotNil(openDialog)}
        onClose={() => setEdited(null)}
        withAdditionalFooter
        title={
          openDialog
            ? t('entity.newVehicle.catalogue.assignmentOnTrim', '{feature} on {trim}', {
                feature: openDialog.edited.feature.name,
                trim: openDialog.edited.trim.name,
              })
            : ''
        }
      >
        <Show when={openDialog}>
          {(dialog) => (
            <AssignmentDialog
              modelYearId={props.modelYearId}
              assignment={dialog.assignment}
              features={props.features}
              variants={props.variants.filter(({trimId}) => trimId === dialog.edited.trim.id)}
              canViewCost={props.canViewCost}
              vatRate={props.vatRate}
              save={(values) => saveDetails(dialog.edited, values)}
              // A save that finishes after its dialog was closed must not close another cell's.
              onDone={() => setEdited((current) => (current === dialog.edited ? null : current))}
            />
          )}
        </Show>
      </Dialog>
      {/* Announces a cell's new value; the cell's own label change is not announced. */}
      <StatusRegion role="status">
        {matrix.lastChange
          ? cellName(
              matrix.lastChange.feature.name,
              matrix.lastChange.trim.name,
              matrix.lastChange.value,
              matrix.lastChange.isUnsaved
            )
          : ''}
      </StatusRegion>
      <Show when={matrix.draftCount > 0}>
        <PendingChangesBar
          title={t(
            'entity.newVehicle.catalogue.unsavedEquipmentChanges',
            '{count} unsaved equipment changes',
            {count: matrix.draftCount}
          )}
          isSaving={matrix.isSaving}
          onDiscard={matrix.discard}
          onSave={() => void matrix.save()}
        />
      </Show>
      <PendingChangesBlocker
        isDirty={matrix.draftCount > 0}
        isSaving={matrix.isSaving}
        shouldBlockNavigation={shouldBlockNavigation}
        onSave={matrix.save}
      />
    </Card>
  );
}
